import React, { useRef, useState } from 'react';
import api from '../api';

const TEMPLATE_COLUMNS = ['학번', '학년', '반', '번호', '이름', '면장반', '면장번호'];
const TEMPLATE_EXAMPLE = ['GBA2026099', '1', '1', '30', '홍길동', '(해당 시)', '(해당 시)'];

// 엑셀 업로드 -> 서버 파싱 결과 미리보기 -> (필요 시 수정) -> 최종 등록, 3단계 흐름의 모달.
export default function StudentImportModal({ onClose, onImported }) {
  const [step, setStep] = useState('pick'); // 'pick' | 'preview'
  const [previewRows, setPreviewRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const fileInputRef = useRef(null);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLoading(true);
    setMessage('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/admin/student-management/import/preview', form);
      const rows = res.data.rows.map((r, i) => ({ ...r, _key: i }));
      setPreviewRows(rows);
      setParseErrors(res.data.errors || []);
      setStep('preview');
    } catch (err) {
      setMessage(err.response?.data?.detail || '엑셀 파일을 읽는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (key, field, value) => {
    setPreviewRows((prev) => prev.map((r) => (r._key === key ? { ...r, [field]: value } : r)));
  };

  const removeRow = (key) => {
    setPreviewRows((prev) => prev.filter((r) => r._key !== key));
  };

  const handleCommit = async () => {
    if (previewRows.length === 0) {
      setMessage('등록할 학생이 없습니다.');
      return;
    }
    const rowsToSend = previewRows.map((r) => ({
      studid: r.studid,
      grade: Number(r.grade),
      class_no: Number(r.class_no),
      studno: r.studno === '' || r.studno == null ? null : Number(r.studno),
      name: r.name,
      mclass: r.mclass === '' || r.mclass == null ? null : Number(r.mclass),
      mstudno: r.mstudno === '' || r.mstudno == null ? null : Number(r.mstudno),
    }));
    setSubmitting(true);
    setMessage('');
    try {
      const res = await api.post('/admin/student-management/import/commit', { rows: rowsToSend });
      onImported(res.data);
    } catch (err) {
      setMessage(err.response?.data?.detail || '등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card import-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>신입생 일괄 등록 (엑셀)</h3>
          <button className="btn-ghost" type="button" onClick={onClose}>닫기</button>
        </div>

        {step === 'pick' && (
          <>
            <p className="form-message">
              아래 순서의 열로 구성된 .xlsx 파일을 준비하세요 (1행은 제목 줄이며 읽지 않습니다).
            </p>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    {TEMPLATE_COLUMNS.map((c) => <th key={c}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {TEMPLATE_EXAMPLE.map((v, i) => <td key={i}>{v}</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="form-message">면장반/면장번호는 해당 없으면 비워두세요.</p>

            {message && <div className="form-message">{message}</div>}

            <div className="comment-actions">
              <button
                className="btn-primary btn-inline"
                type="button"
                onClick={handlePickFile}
                disabled={loading}
              >
                {loading ? '파일 확인 중...' : '엑셀 파일 선택'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
          </>
        )}

        {step === 'preview' && (
          <>
            <p className="form-message">
              {previewRows.length}건 확인됨 — 필요한 부분을 수정하거나 제외한 뒤 등록하세요.
            </p>
            {parseErrors.length > 0 && (
              <div className="form-message">
                건너뛴 행 {parseErrors.length}건: {parseErrors.slice(0, 5).join(' / ')}
              </div>
            )}

            <div className="table-scroll import-preview-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>학번</th>
                    <th>학년</th>
                    <th>반</th>
                    <th>번호</th>
                    <th>이름</th>
                    <th>면장반</th>
                    <th>면장번호</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r) => (
                    <tr key={r._key} className={r.duplicate ? 'import-row-duplicate' : ''}>
                      <td>
                        <input
                          className="field-input"
                          value={r.studid}
                          onChange={(e) => updateRow(r._key, 'studid', e.target.value)}
                        />
                        {r.duplicate && <div className="import-dup-hint">이미 등록된 학번(건너뜀)</div>}
                      </td>
                      <td>
                        <select
                          className="field-input"
                          value={r.grade}
                          onChange={(e) => updateRow(r._key, 'grade', e.target.value)}
                        >
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                          <option value={3}>3</option>
                        </select>
                      </td>
                      <td>
                        <input
                          className="field-input"
                          type="number"
                          min="1"
                          value={r.class_no}
                          onChange={(e) => updateRow(r._key, 'class_no', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="field-input"
                          type="number"
                          min="1"
                          value={r.studno ?? ''}
                          onChange={(e) => updateRow(r._key, 'studno', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="field-input"
                          value={r.name}
                          onChange={(e) => updateRow(r._key, 'name', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="field-input"
                          type="number"
                          min="1"
                          value={r.mclass ?? ''}
                          onChange={(e) => updateRow(r._key, 'mclass', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="field-input"
                          type="number"
                          min="1"
                          value={r.mstudno ?? ''}
                          onChange={(e) => updateRow(r._key, 'mstudno', e.target.value)}
                        />
                      </td>
                      <td>
                        <button className="btn-link" type="button" onClick={() => removeRow(r._key)}>제외</button>
                      </td>
                    </tr>
                  ))}
                  {previewRows.length === 0 && (
                    <tr>
                      <td colSpan="8" className="empty-row">등록할 행이 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {message && <div className="form-message">{message}</div>}

            <div className="comment-actions">
              <button
                className="btn-primary btn-inline"
                type="button"
                onClick={handleCommit}
                disabled={submitting || previewRows.length === 0}
              >
                {submitting ? '등록 중...' : `${previewRows.length}건 최종 등록`}
              </button>
              <button className="btn-ghost" type="button" onClick={() => setStep('pick')}>
                다른 파일 선택
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
